import React from 'react';
import { Box, Typography, Container, Grid, Button, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SecurityIcon from '@mui/icons-material/Security';
import CodeIcon from '@mui/icons-material/Code';
import RouterIcon from '@mui/icons-material/Router';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';

const Hero = () => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleWhatsApp = (service) => {
    const phoneNumber = '+995599400135';
    const message = service 
      ? encodeURIComponent(`Hello! I am interested in your ${service} services.`)
      : encodeURIComponent('Hello! I would like to inquire about your IT services.');
    window.open(`https://wa.me/${phoneNumber}?text=${message}`);
    handleClose();
  };

  const services = [
    { name: 'IT Security', icon: <SecurityIcon fontSize="small" /> },
    { name: 'Web Development', icon: <CodeIcon fontSize="small" /> },
    { name: 'Network Security', icon: <RouterIcon fontSize="small" /> },
    { name: 'Software Solutions', icon: <SettingsSuggestIcon fontSize="small" /> },
    { name: 'General Inquiry', icon: <ContactSupportIcon fontSize="small" /> },
  ];

  const scrollToServices = () => {
    document.getElementById('services').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      id="home"
      sx={{
        py: { xs: 8, md: 12 },
        background: isDarkMode
          ? 'linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(18, 18, 18, 0.95) 100%)'
          : 'linear-gradient(135deg, rgba(46, 91, 255, 0.05) 0%, rgba(0, 200, 150, 0.05) 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background elements */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          opacity: 0.1,
          background: `radial-gradient(circle at 20% 30%, ${isDarkMode ? 'rgba(46, 91, 255, 0.4)' : 'rgba(46, 91, 255, 0.2)'} 0%, transparent 25%),
                      radial-gradient(circle at 80% 70%, ${isDarkMode ? 'rgba(0, 200, 150, 0.4)' : 'rgba(0, 200, 150, 0.2)'} 0%, transparent 25%)`,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={7}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 700,
                  mb: 2,
                  background: 'linear-gradient(45deg, #2E5BFF, #00C896)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('heroTitle')}
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Typography
                variant="h5"
                component="p"
                sx={{
                  mb: 4,
                  color: isDarkMode ? 'text.secondary' : 'text.primary',
                  lineHeight: 1.6,
                }}
              >
                {t('heroSubtitle')}
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  onClick={handleClick}
                  startIcon={<WhatsAppIcon />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '1rem',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  {t('contactUs')}
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  PaperProps={{
                    sx: {
                      borderRadius: 2,
                      mt: 1,
                      minWidth: 220,
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    }
                  }}
                >
                  <Typography variant="overline" sx={{ px: 2, py: 1, display: 'block', color: 'text.secondary', fontWeight: 700 }}>
                    Select a Service
                  </Typography>
                  {services.map((service) => (
                    <MenuItem key={service.name} onClick={() => handleWhatsApp(service.name)}>
                      <ListItemIcon>
                        {service.icon}
                      </ListItemIcon>
                      <ListItemText primary={service.name} />
                    </MenuItem>
                  ))}
                </Menu>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={scrollToServices}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '1rem',
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                    },
                  }}
                >
                  {t('learnMore')}
                </Button>
              </Box>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={5}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                <Box 
                  component="img" 
                  src="/logo.png" 
                  alt="ITFORSEC Premium Logo" 
                  sx={{ 
                    width: '100%',
                    height: 'auto',
                    zIndex: 1,
                    borderRadius: 2, // Keep a small radius for a clean look if the image is square
                  }} 
                />
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Hero;
